export interface FeedAuthor {
  id?: string | null;
  name?: string | null;
  avatar?: {uri?: string | null} | null;
}

export interface FeedPost {
  id: string;
  message?: string | null;
  createdAt?: string | null;
  likesTotal?: number | null;
  liked?: boolean | null;
  pinned?: boolean | null;
  author?: FeedAuthor | null;
  image?: {uri?: string | null} | null;
  comments?: {total?: number | null} | null;
}

export interface FeedPage {
  list: FeedPost[];
  endCursor?: string | null;
  hasNextPage: boolean;
}

// O PostType serializa created_at com String#to_s, que devolve
// "2026-08-09 00:59:40 -0300" — nao e ISO8601. O V8 tolera, mas Safari e
// Firefox devolvem Invalid Date, entao a string e normalizada antes.
export const parsePostDate = (valor?: string | null): Date | null => {
  if (!valor) return null;

  const normalizado = valor
    .trim()
    .replace(' ', 'T')
    .replace(/\s([+-]\d{2}):?(\d{2})$/, '$1:$2')
    .replace(/\s?UTC$/, 'Z');

  const data = new Date(normalizado);
  if (!Number.isNaN(data.getTime())) return data;

  // Ultimo recurso: deixa o parser nativo tentar a string original.
  const bruta = new Date(valor);
  return Number.isNaN(bruta.getTime()) ? null : bruta;
};

const MINUTO = 60;
const HORA = 3600;
const DIA = 86400;
const SEMANA = 604800;

export const formatRelativeTime = (valor?: string | null, agora: Date = new Date()): string => {
  const data = parsePostDate(valor);
  if (!data) return '';

  const segundos = Math.floor((agora.getTime() - data.getTime()) / 1000);

  // Relogio do dispositivo atrasado faz o post parecer futuro; tratar como agora.
  if (segundos < MINUTO) return 'agora';
  if (segundos < HORA) return `${Math.floor(segundos / MINUTO)} min`;
  if (segundos < DIA) return `${Math.floor(segundos / HORA)} h`;
  if (segundos < SEMANA) return `${Math.floor(segundos / DIA)} d`;

  return data.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'});
};

// A paginacao e por cursor keyset, e o backend pode devolver um item repetido
// quando um post e criado entre duas paginas. Deduplicar por id evita o warning
// de key duplicada do React e o post aparecendo duas vezes na tela.
export const mergeFeedPages = (anterior: FeedPage | undefined, proxima: FeedPage): FeedPage => {
  const listaAnterior = anterior?.list ?? [];
  const vistos = new Set(listaAnterior.map((post) => post.id));
  const novos = (proxima.list ?? []).filter((post) => post.id && !vistos.has(post.id));

  return {
    list: [...listaAnterior, ...novos],
    endCursor: proxima.endCursor,
    hasNextPage: proxima.hasNextPage,
  };
};

export type AcaoDeCurtida = 'like' | 'dislike';

export const acaoDeCurtida = (post: FeedPost): AcaoDeCurtida => (post.liked ? 'dislike' : 'like');

// A resposta otimista pinta o coracao antes de o servidor responder. O total
// precisa acompanhar, senao o numero so mudaria no proximo carregamento.
// Math.max protege contra total negativo se o contador ja estiver defasado.
export const curtidaOtimista = (post: FeedPost, acao: AcaoDeCurtida): {liked: boolean; likesTotal: number} => {
  const total = post.likesTotal ?? 0;

  return acao === 'like' ? {liked: true, likesTotal: total + 1} : {liked: false, likesTotal: Math.max(0, total - 1)};
};

export const postAuthorName = (post: FeedPost): string => post.author?.name?.trim() || 'Anônimo';

export const postAuthorInitial = (post: FeedPost): string =>
  postAuthorName(post).charAt(0).toLocaleUpperCase('pt-BR');

export interface FeedComment {
  id: string;
  message: string;
  author?: FeedAuthor | null;
}

// Mesma regra do post: comentario de autor apagado nao vira card sem nome.
export const commentAuthorName = (comment: FeedComment): string => comment.author?.name?.trim() || 'Anônimo';

export const commentAuthorInitial = (comment: FeedComment): string =>
  commentAuthorName(comment).charAt(0).toLocaleUpperCase('pt-BR');
